import type { Session, User } from '@supabase/supabase-js';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { Tables } from '@/lib/supabase/types';

export type MembershipWithOrganization = Tables<'memberships'> & {
  organization: Tables<'organizations'> | null;
};

interface SessionState {
  bootstrapped: boolean;
  session: Session | null;
  user: User | null;
  profile: Tables<'profiles'> | null;
  memberships: MembershipWithOrganization[];
  activeOrganizationId: string | null;
  hydrate: (payload: {
    bootstrapped: boolean;
    session: Session | null;
    user: User | null;
    profile: Tables<'profiles'> | null;
    memberships: MembershipWithOrganization[];
    activeOrganizationId?: string | null;
  }) => void;
  setActiveOrganizationId: (organizationId: string) => void;
  setBootstrapped: () => void;
  clear: () => void;
}

const safeStorage =
  typeof window !== 'undefined'
    ? createJSONStorage(() => window.localStorage)
    : undefined;

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      bootstrapped: false,
      session: null,
      user: null,
      profile: null,
      memberships: [],
      activeOrganizationId: null,
      hydrate: ({ bootstrapped, session, user, profile, memberships, activeOrganizationId }) =>
        set((state) => ({
          bootstrapped,
          session,
          user,
          profile,
          memberships,
          activeOrganizationId:
            activeOrganizationId ??
            state.activeOrganizationId ??
            memberships.find((membership) => membership.is_default)?.organization_id ??
            memberships[0]?.organization_id ??
            null,
        })),
      setActiveOrganizationId: (activeOrganizationId) => set({ activeOrganizationId }),
      setBootstrapped: () => set({ bootstrapped: true }),
      clear: () =>
        set({
          bootstrapped: true,
          session: null,
          user: null,
          profile: null,
          memberships: [],
          activeOrganizationId: null,
        }),
    }),
    {
      name: 'nordiccompanyhub-session',
      storage: safeStorage,
      partialize: (state) => ({
        activeOrganizationId: state.activeOrganizationId,
      }),
    },
  ),
);

export function selectActiveRole(state: SessionState) {
  return state.memberships.find((membership) => membership.organization_id === state.activeOrganizationId)?.role ?? null;
}

